import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import {
  Option,
  OptionAction,
  OptionDescription,
  OptionIcon,
  OptionsList,
  OptionTitle,
} from "@babackapp/customer-ui/components/option";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { EnterIcon } from "@radix-ui/react-icons";
import { ArrowLeft, ChevronRight, RefreshCcw, WalletIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useLineItemResolutionForm } from "../../hooks/use-line-item-resolution-form";
import {
  getAutoCommittableLeaf,
  type SingleFamilyLeaf,
} from "@babackapp/return-widget-components/lib/resolutions.lib";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { UserPortalCallout } from "@babackapp/return-widget-components/components/user-portal-callout";
import { useUpdateLineItem } from "@babackapp/return-widget-components/hooks/use-update-line-item";
import { useWidgetParams } from "../../hooks/use-widget-params";
import { useResolutionSuggestions } from "../../hooks/use-resolution-suggestions";
import type { SuggestionItem } from "../../hooks/use-resolution-suggestions";
import { PromotedLeafTile } from "./promoted-leaf-tile";
import { SuggestionGrid } from "./suggestion-grid";

export function Resolution() {
  const {
    form,
    onSubmit,
    commitLeaf,
    rawResolutions,
    availableResolutions,
    isRetractationItem,
    formattedShippingCost,
    userPortalTexts,
  } = useLineItemResolutionForm();
  const { lineItemId = "" } = useParams<{ lineItemId: string }>();
  const { orderId } = useWidgetParams();
  const updateLineItem = useUpdateLineItem(orderId);
  const navigate = useNavigate();
  const suggestions = useResolutionSuggestions();
  const t = useCustomText();
  const [selectedLeaf, setSelectedLeaf] =
    useState<NonNullable<SingleFamilyLeaf> | null>(null);

  const canExchange = availableResolutions.includes("exchange");

  const RESOLUTION_CONFIG: Record<
    "refund" | "exchange",
    { icon: React.ReactNode; title: string; description: string }
  > = {
    refund: {
      icon: <WalletIcon />,
      title: t("resolution.refund.title"),
      description: isRetractationItem
        ? t("returnMethod.retractation.costInfo", { amount: formattedShippingCost })
        : t("resolution.refund.description"),
    },
    exchange: {
      icon: <RefreshCcw />,
      title: t("resolution.exchange.title"),
      description: t("resolution.exchange.description"),
    },
  };

  // Hook will navigate away when only one resolution — render nothing in the meantime
  if (availableResolutions.length <= 1) return null;

  const currentVariant = suggestions?.currentVariantTitle;
  const suggestionTitle =
    suggestions?.type === "sizing"
      ? currentVariant
        ? t("suggestions.sizing.titleWithVariant", { variant: currentVariant })
        : t("suggestions.sizing.title")
      : t("suggestions.products.title");

  function handleSuggestionSelect(item: SuggestionItem) {
    if (item.variantId) {
      updateLineItem(lineItemId, {
        resolution: "exchange",
        exchangeVariantId: item.variantId,
      });
      navigate("../../../summary");
      return;
    }
    navigate(`../product-selection?productId=${item.id}`, {
      state: { imageUrl: item.imageUrl, label: item.label },
    });
  }

  function handleSelectResolution(value: "refund" | "exchange") {
    form.setValue("resolution", value);
    onSubmit();
  }

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{t("resolution.title")}</HeaderTitle>
        <HeaderDescription>{t("resolution.description")}</HeaderDescription>
      </Header>
      <UserPortalCallout texts={userPortalTexts} />

      <AnimatePresence mode="wait">
        {canExchange && suggestions && suggestions.items.length > 0 && (
          <motion.div
            key="suggestion-grid"
            initial={{ opacity: 0, filter: "blur(8px)" }}
            animate={{ opacity: 1, filter: "blur(0px)", transition: { duration: 0.4, ease: "easeOut" } }}
            exit={{ opacity: 0, filter: "blur(8px)", transition: { duration: 0.25, ease: "easeIn" } }}
          >
            <SuggestionGrid
              title={suggestionTitle}
              items={suggestions.items}
              onSelect={handleSuggestionSelect}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <OptionsList>
        {availableResolutions.map((value) => {
          const autoLeaf = getAutoCommittableLeaf(value, rawResolutions);
          if (autoLeaf) {
            return (
              <PromotedLeafTile
                key={value}
                leaf={autoLeaf}
                selected={selectedLeaf?.resolution === autoLeaf.resolution}
                onSelect={() => setSelectedLeaf(autoLeaf)}
              />
            );
          }
          const config = RESOLUTION_CONFIG[value];
          return (
            <button
              key={value}
              type="button"
              className="w-full text-left"
              onClick={() => handleSelectResolution(value)}
            >
              <Option>
                <OptionIcon>{config.icon}</OptionIcon>
                <OptionTitle>{config.title}</OptionTitle>
                <OptionDescription>{config.description}</OptionDescription>
                <OptionAction>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </OptionAction>
              </Option>
            </button>
          );
        })}
      </OptionsList>

      <AnimatedButtonRow variants={{}}>
        <AnimatedButtonContainer
          layoutId="line-item-back-button"
          disableVariants={{ exit: true }}
        >
          <Link to="../reason">
            <Button variant="secondary" type="button">
              <ArrowLeft />
              {t("common.back")}
            </Button>
          </Link>
        </AnimatedButtonContainer>
        {selectedLeaf && (
          <AnimatedButtonContainer
            layoutId="line-item-confirm-button"
            disableVariants={{ enter: true, exit: true }}
          >
            <Button type="button" onClick={() => commitLeaf(selectedLeaf)}>
              <EnterIcon />
              {t("common.confirm")}
            </Button>
          </AnimatedButtonContainer>
        )}
      </AnimatedButtonRow>
    </WidgetPage>
  );
}
